import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import * as LucideIcons from "lucide-react";
import { ArrowRight, Check, ChevronDown, Sparkles } from "lucide-react";
import { makeT, DEFAULT_TERMS } from "@/lib/terminology";
import { BlockRenderer } from "@/components/builder/BlockRenderer";
import TrackingScripts from "@/components/TrackingScripts";
import PlatformLogo from "@/components/PlatformLogo";

/* -------------------------------------------------------------------------- */
/*  Public landing page — driven entirely by /api/public/platform-content     */
/* -------------------------------------------------------------------------- */

export default function LandingPage() {
  const [content, setContent] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [activeIndustry, setActiveIndustry] = useState(null);

  useEffect(() => {
    api.get("/public/platform-content").then(({ data }) => setContent(data));
    api.get("/public/industries").then(({ data }) => setIndustries(data || []));
  }, []);

  // Apply colors + SEO whenever content arrives
  useEffect(() => {
    if (!content) return;
    const c = content.colors || {};
    const root = document.documentElement;
    if (c.primary) root.style.setProperty("--lp-primary", c.primary);
    if (c.bg) root.style.setProperty("--lp-bg", c.bg);
    if (c.text) root.style.setProperty("--lp-text", c.text);
    if (c.accent) root.style.setProperty("--lp-accent", c.accent);
    if (c.accent2) root.style.setProperty("--lp-accent2", c.accent2);
    if (c.muted) root.style.setProperty("--lp-muted", c.muted);
    if (content.seo?.title) document.title = content.seo.title;
    const desc = document.querySelector('meta[name="description"]') || document.createElement("meta");
    desc.setAttribute("name", "description");
    desc.setAttribute("content", content.seo?.description || "");
    if (!desc.parentNode) document.head.appendChild(desc);
  }, [content]);

  if (!content) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", color: "#6B7280" }}>
        Lade ...
      </div>
    );
  }

  const { colors = {}, hero = {}, trust = {}, features = {}, benefits = {}, pricing = {}, testimonials = {}, faq = {}, cta_final = {}, footer = {} } = content;

  // Live terminology — when an industry is picked, key words swap automatically
  const t = makeT(activeIndustry?.terminology);

  return (
    <div data-testid="landing-page" style={{
      background: "var(--lp-bg, #FFFFFF)",
      color: "var(--lp-text, #0A0A0A)",
      fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, sans-serif",
      minHeight: "100vh",
    }}>
      <TrackingScripts
        gaId={process.env.REACT_APP_PLATFORM_GA_ID || ""}
        metaPixelId={process.env.REACT_APP_PLATFORM_META_PIXEL_ID || ""}
      />
      <LandingNav />
      <Hero hero={hero} t={t} activeIndustry={activeIndustry} />
      <IndustrySwitcher
        industries={industries}
        active={activeIndustry}
        onSelect={setActiveIndustry}
      />
      <TrustBar trust={trust} />
      <Features features={features} t={t} />
      <Benefits benefits={benefits} />
      <PricingTeaser pricing={pricing} />
      <Testimonials testimonials={testimonials} />
      <FAQ faq={faq} />
      <CustomBlocks blocks={content.blocks || []} />
      <FinalCTA cta={cta_final} />
      <LandingFooter footer={footer} />
    </div>
  );
}

/* ============== components ============== */

function LandingNav() {
  return (
    <nav style={{
      position: "sticky", top: 0, zIndex: 30,
      background: "rgba(255,255,255,0.78)", backdropFilter: "blur(20px)",
      borderBottom: "1px solid rgba(0,0,0,0.06)",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" data-testid="landing-logo" style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--lp-primary)", fontWeight: 700, letterSpacing: -0.5, fontSize: 22 }}>
          <PlatformLogo imgClassName="h-8 w-auto object-contain">
            <span style={{ display: "inline-block", width: 28, height: 28, borderRadius: 8, background: "linear-gradient(135deg, var(--lp-accent) 0%, var(--lp-accent2) 100%)" }} />
            StudioMV
          </PlatformLogo>
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Link to="/pricing" data-testid="nav-pricing" style={{ fontSize: 14, color: "var(--lp-text)", opacity: 0.7 }}>Pakete</Link>
          <Link to="/login" data-testid="nav-login" style={{ fontSize: 14, color: "var(--lp-text)", opacity: 0.7 }}>Anmelden</Link>
          <Link to="/register" data-testid="nav-cta" style={btnSolid()}>
            Kostenlos starten <ArrowRight style={{ width: 14, height: 14 }} />
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero({ hero, t, activeIndustry }) {
  const swap = (text) => substituteTerms(text, t);
  const layout = hero.layout || "split";
  const hasImage = !!hero.image_url;
  const overlayOpacity = typeof hero.overlay_opacity === "number" ? hero.overlay_opacity : 0.55;

  // Variant: full-width background image (edge-to-edge banner section)
  // Variant: fullscreen background image (covers entire viewport, 100vh)
  if (hasImage && (layout === "background" || layout === "fullscreen")) {
    const isFs = layout === "fullscreen";
    return (
      <section
        data-testid="landing-hero"
        data-hero-layout={layout}
        style={{
          position: "relative",
          overflow: "hidden",
          minHeight: isFs ? "100vh" : "min(720px, 80vh)",
          display: "flex",
          alignItems: "center",
          color: "#fff",
        }}
      >
        <img
          src={hero.image_url}
          alt=""
          aria-hidden
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute", inset: 0, zIndex: 1,
            background: `linear-gradient(180deg, rgba(8,12,20,${overlayOpacity * 0.65}) 0%, rgba(8,12,20,${overlayOpacity}) 100%)`,
          }}
        />
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: isFs ? "120px 24px" : "96px 24px", position: "relative", zIndex: 2, width: "100%" }}>
          <div style={{ maxWidth: 820 }}>
            {hero.eyebrow && (
              <p style={{ ...eyebrowStyle(), color: "rgba(255,255,255,0.85)" }} data-testid="hero-eyebrow-text">{swap(hero.eyebrow)}</p>
            )}
            <h1 data-testid="hero-headline-text" style={{
              fontSize: "clamp(40px, 6.5vw, 84px)", lineHeight: 1.04, fontWeight: 700, letterSpacing: -1.5,
              marginTop: 18, marginBottom: 24, color: "#fff",
              textShadow: "0 2px 24px rgba(0,0,0,0.35)",
            }}>
              {swap(hero.headline)}
            </h1>
            <p data-testid="hero-subline-text" style={{ fontSize: 19, lineHeight: 1.55, color: "rgba(255,255,255,0.88)", maxWidth: 620, marginBottom: 36 }}>
              {activeIndustry
                ? `Speziell für ${activeIndustry.name}. ${activeIndustry.description || swap(hero.subline)}`
                : swap(hero.subline)}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {hero.cta_primary?.label && (
                <Link data-testid="hero-cta-primary" to={hero.cta_primary.url || "/register"} style={btnSolid("lg")}>
                  {hero.cta_primary.label} <ArrowRight style={{ width: 16, height: 16 }} />
                </Link>
              )}
              {hero.cta_secondary?.label && (
                <Link data-testid="hero-cta-secondary" to={hero.cta_secondary.url || "/pricing"} style={{ ...btnOutline("lg"), color: "#fff", borderColor: "rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.08)" }}>
                  {hero.cta_secondary.label}
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default: split layout (image right, text left) — also used when no image
  return (
    <section data-testid="landing-hero" data-hero-layout="split" style={{ position: "relative", overflow: "hidden" }}>
      {/* Background gradient */}
      <div aria-hidden style={{ position: "absolute", inset: 0, background:
        "radial-gradient(circle at 20% 10%, rgba(20,184,166,0.18) 0%, transparent 45%), radial-gradient(circle at 80% 60%, rgba(6,182,212,0.18) 0%, transparent 50%)",
        pointerEvents: "none" }} />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "96px 24px 60px", position: "relative" }}>
        <div style={{ display: "grid", gridTemplateColumns: hasImage ? "1.1fr 1fr" : "1fr", gap: 60, alignItems: "center" }}>
          <div>
            {hero.eyebrow && (
              <p style={eyebrowStyle()} data-testid="hero-eyebrow-text">{swap(hero.eyebrow)}</p>
            )}
            <h1 data-testid="hero-headline-text" style={{
              fontSize: "clamp(38px, 6vw, 76px)", lineHeight: 1.04, fontWeight: 700, letterSpacing: -1.5,
              marginTop: 18, marginBottom: 24,
            }}>
              {swap(hero.headline)}
            </h1>
            <p data-testid="hero-subline-text" style={{ fontSize: 18, lineHeight: 1.55, color: "var(--lp-muted, #6B7280)", maxWidth: 580, marginBottom: 36 }}>
              {activeIndustry
                ? `Speziell für ${activeIndustry.name}. ${activeIndustry.description || swap(hero.subline)}`
                : swap(hero.subline)}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {hero.cta_primary?.label && (
                <Link data-testid="hero-cta-primary" to={hero.cta_primary.url || "/register"} style={btnSolid("lg")}>
                  {hero.cta_primary.label} <ArrowRight style={{ width: 16, height: 16 }} />
                </Link>
              )}
              {hero.cta_secondary?.label && (
                <Link data-testid="hero-cta-secondary" to={hero.cta_secondary.url || "/pricing"} style={btnOutline("lg")}>
                  {hero.cta_secondary.label}
                </Link>
              )}
            </div>
          </div>
          {hasImage && (
            <div style={{ position: "relative" }}>
              <div style={{
                position: "absolute", inset: "8px -16px -16px 8px",
                background: "linear-gradient(135deg, var(--lp-accent) 0%, var(--lp-accent2) 100%)",
                borderRadius: 24, opacity: 0.18,
              }} />
              <img src={hero.image_url} alt="" style={{ width: "100%", borderRadius: 20, position: "relative", boxShadow: "0 40px 80px -20px rgba(0,0,0,0.25)" }} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function IndustrySwitcher({ industries, active, onSelect }) {
  if (!industries?.length) return null;
  // Show up to 4 chips where the override is DIFFERENT from the default,
  // so we don't surface noisy "Kunde → Kunde" no-ops.
  const sample = active && active.terminology
    ? Object.entries(active.terminology)
        .filter(([k, v]) => v && DEFAULT_TERMS[k] && DEFAULT_TERMS[k] !== v)
        .slice(0, 4)
    : [];
  return (
    <section data-testid="landing-industry-switcher" style={{
      padding: "20px 24px 60px", position: "relative",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <p style={{ ...eyebrowStyle(), textAlign: "center", marginBottom: 14 }}>
          DEIN BERUF — WÄHLE UND SIEH DAS WORDING LIVE
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
          <button
            data-testid="industry-switcher-clear"
            onClick={() => onSelect(null)}
            style={industryPill(!active)}
          >
            Allgemein
          </button>
          {industries.map((i) => {
            const Icon = LucideIcons[i.icon] || LucideIcons.Sparkles;
            const isOn = active?.id === i.id;
            return (
              <button
                key={i.id}
                data-testid={`industry-switcher-${i.slug}`}
                onClick={() => onSelect(i)}
                style={industryPill(isOn)}
              >
                <Icon style={{ width: 14, height: 14 }} strokeWidth={1.75} />
                {i.name}
              </button>
            );
          })}
        </div>

        {sample.length > 0 && (
          <div data-testid="industry-switcher-preview" style={{
            marginTop: 22, display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center",
          }}>
            {sample.map(([key, val]) => (
              <span key={key} style={{
                fontSize: 12, padding: "6px 12px", borderRadius: 999,
                background: "rgba(20,184,166,0.10)",
                border: "1px solid rgba(20,184,166,0.30)",
                color: "var(--lp-text)",
              }}>
                <span style={{ opacity: 0.55, textDecoration: "line-through" }}>{DEFAULT_TERMS[key] || key}</span>
                {" → "}
                <strong style={{ color: "var(--lp-accent)" }}>{val}</strong>
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function industryPill(active) {
  return {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "8px 14px", borderRadius: 999, cursor: "pointer",
    fontSize: 13, fontWeight: 500, fontFamily: "inherit",
    background: active ? "var(--lp-primary)" : "transparent",
    color: active ? "#fff" : "var(--lp-text)",
    border: active ? "1px solid var(--lp-primary)" : "1px solid rgba(0,0,0,0.12)",
    transition: "all 0.18s ease",
  };
}

/* Replace default German terms inside free text with the active terminology.
   Conservative: only matches WHOLE words (with German singular/plural variants)
   so we never accidentally substitute substrings inside brand names. */
function substituteTerms(text, t) {
  if (!text) return "";
  // Keys most likely to appear in marketing copy
  const KEYS = [
    "staff", "staff_plural", "stylist", "service", "service_plural",
    "booking", "booking_plural", "appointment", "appointment_plural",
    "customer", "customer_plural", "salon", "salon_plural",
  ];
  let out = text;
  for (const k of KEYS) {
    const def = DEFAULT_TERMS[k];
    const repl = t(k);
    if (!def || !repl || def === repl) continue;
    // Word-boundary replacement (case-insensitive, preserves leading capital)
    const re = new RegExp(`\\b${escapeRe(def)}\\b`, "gi");
    out = out.replace(re, (m) => preserveCase(m, repl));
  }
  return out;
}
function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }
function preserveCase(orig, repl) {
  if (orig[0] === orig[0].toUpperCase()) return repl[0].toUpperCase() + repl.slice(1);
  return repl;
}

function TrustBar({ trust }) {
  if (!trust?.stats?.length) return null;
  return (
    <section data-testid="landing-trust" style={{ borderTop: "1px solid rgba(0,0,0,0.06)", borderBottom: "1px solid rgba(0,0,0,0.06)", background: "rgba(0,0,0,0.015)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
        {trust.label && <p style={{ ...eyebrowStyle(), textAlign: "center", marginBottom: 24 }}>{trust.label}</p>}
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(2, trust.stats.length)}, 1fr)`, gap: 32, textAlign: "center" }}>
          {trust.stats.map((s, i) => (
            <div key={i}>
              <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: -1, background: "linear-gradient(135deg, var(--lp-accent) 0%, var(--lp-accent2) 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>{s.value}</div>
              <div style={{ fontSize: 13, color: "var(--lp-muted)", marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features({ features, t }) {
  const swap = (text) => substituteTerms(text, t);
  return (
    <section data-testid="landing-features" style={{ padding: "120px 0" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <SectionHeader eyebrow={features.eyebrow} headline={swap(features.headline)} subline={swap(features.subline)} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24, marginTop: 60 }}>
          {(features.items || []).map((f, i) => {
            const Icon = LucideIcons[f.icon] || Sparkles;
            return (
              <article key={i} data-testid={`feature-${i}`} style={{
                padding: 28, borderRadius: 18,
                background: "var(--lp-bg)",
                border: "1px solid rgba(0,0,0,0.08)",
                transition: "transform 0.2s, box-shadow 0.2s",
              }} onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 20px 40px -10px rgba(0,0,0,0.12)"; }}
                 onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14, display: "grid", placeItems: "center",
                  background: "linear-gradient(135deg, var(--lp-accent) 0%, var(--lp-accent2) 100%)",
                  color: "#fff", marginBottom: 18,
                }}>
                  <Icon style={{ width: 22, height: 22 }} strokeWidth={1.75} />
                </div>
                <h3 style={{ fontSize: 19, fontWeight: 600, marginBottom: 8, letterSpacing: -0.3 }}>{swap(f.title)}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: "var(--lp-muted)" }}>{swap(f.description)}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Benefits({ benefits }) {
  return (
    <section data-testid="landing-benefits" style={{
      padding: "120px 0",
      background: "var(--lp-primary, #0A0A0A)",
      color: "#fff",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <SectionHeader eyebrow={benefits.eyebrow} headline={benefits.headline} dark />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 32, marginTop: 60 }}>
          {(benefits.items || []).map((b, i) => (
            <div key={i} data-testid={`benefit-${i}`} style={{ padding: "28px 0" }}>
              <div style={{
                fontSize: 64, fontWeight: 700, letterSpacing: -2, lineHeight: 1,
                background: "linear-gradient(135deg, var(--lp-accent) 0%, var(--lp-accent2) 100%)",
                WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
                marginBottom: 12,
              }}>{b.stat}</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{b.label}</div>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: "rgba(255,255,255,0.6)" }}>{b.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingTeaser({ pricing }) {
  return (
    <section data-testid="landing-pricing" style={{ padding: "120px 0" }}>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
        <SectionHeader eyebrow={pricing.eyebrow} headline={pricing.headline} subline={pricing.subline} centered />
        <Link to={pricing.cta_url || "/pricing"} data-testid="pricing-cta" style={{ ...btnSolid("lg"), marginTop: 36 }}>
          {pricing.cta_label || "Alle Pakete ansehen"} <ArrowRight style={{ width: 16, height: 16 }} />
        </Link>
      </div>
    </section>
  );
}

function Testimonials({ testimonials }) {
  if (!testimonials?.items?.length) return null;
  return (
    <section data-testid="landing-testimonials" style={{ padding: "120px 0", background: "rgba(0,0,0,0.025)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <SectionHeader eyebrow={testimonials.eyebrow} headline={testimonials.headline} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, marginTop: 60 }}>
          {testimonials.items.map((t, i) => (
            <article key={i} data-testid={`testimonial-${i}`} style={{
              padding: 32, borderRadius: 20, background: "#fff",
              border: "1px solid rgba(0,0,0,0.06)",
              display: "flex", flexDirection: "column", gap: 18,
            }}>
              <div aria-hidden style={{ fontSize: 36, lineHeight: 1, color: "var(--lp-accent)", fontFamily: "serif" }}>&ldquo;</div>
              <blockquote style={{ fontSize: 16, lineHeight: 1.6, color: "var(--lp-text)", margin: 0 }}>
                {t.quote}
              </blockquote>
              <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: "50%",
                  background: t.avatar_url ? `center/cover url(${t.avatar_url})` : "linear-gradient(135deg, var(--lp-accent) 0%, var(--lp-accent2) 100%)",
                  display: "grid", placeItems: "center", color: "#fff", fontWeight: 700, fontSize: 16,
                }}>
                  {!t.avatar_url && (t.name || "?").charAt(0)}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: "var(--lp-muted)" }}>{[t.role, t.salon].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ({ faq }) {
  const [open, setOpen] = useState(0);
  if (!faq?.items?.length) return null;
  return (
    <section data-testid="landing-faq" style={{ padding: "120px 0" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <SectionHeader eyebrow={faq.eyebrow} headline={faq.headline} centered />
        <div style={{ marginTop: 50, display: "flex", flexDirection: "column", gap: 12 }}>
          {faq.items.map((q, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`faq-${i}`} style={{
                borderRadius: 14, border: "1px solid rgba(0,0,0,0.08)",
                overflow: "hidden", background: "#fff",
              }}>
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  style={{
                    width: "100%", padding: "20px 24px", border: "none", background: "transparent",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    fontSize: 16, fontWeight: 600, cursor: "pointer", color: "inherit", textAlign: "left",
                  }}
                >
                  <span>{q.question}</span>
                  <ChevronDown style={{ width: 18, height: 18, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", color: "var(--lp-accent)" }} />
                </button>
                {isOpen && (
                  <div style={{ padding: "0 24px 20px", fontSize: 14, lineHeight: 1.6, color: "var(--lp-muted)" }}>
                    {q.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ cta }) {
  return (
    <section data-testid="landing-final-cta" style={{
      padding: "100px 24px", textAlign: "center",
      background: "linear-gradient(135deg, var(--lp-accent) 0%, var(--lp-accent2) 100%)",
      color: "#fff",
    }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {cta.eyebrow && <p style={{ ...eyebrowStyle(), color: "rgba(255,255,255,0.7)" }}>{cta.eyebrow}</p>}
        <h2 style={{ fontSize: "clamp(32px, 4vw, 56px)", fontWeight: 700, letterSpacing: -1, lineHeight: 1.1, marginTop: 16, marginBottom: 20 }}>
          {cta.headline}
        </h2>
        {cta.subline && <p style={{ fontSize: 17, opacity: 0.85, marginBottom: 32 }}>{cta.subline}</p>}
        <Link to={cta.cta_url || "/register"} data-testid="final-cta-btn" style={{
          ...btnSolid("lg"),
          background: "#fff", color: "var(--lp-primary)",
        }}>
          {cta.cta_label || "Jetzt starten"} <ArrowRight style={{ width: 16, height: 16 }} />
        </Link>
      </div>
    </section>
  );
}

function CustomBlocks({ blocks }) {
  if (!blocks?.length) return null;
  return (
    <section data-testid="landing-custom-blocks">
      {blocks.map((b, i) => (
        <div key={b.id || i} data-testid={`custom-block-${i}`}>
          {/* full-width blocks render their own edge-to-edge; others get a container */}
          {b.type === "image_fullwidth" || b.type === "hero" ? (
            <BlockRenderer block={b} theme={{}} />
          ) : (
            <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 24px" }}>
              <BlockRenderer block={b} theme={{}} />
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

function LandingFooter({ footer }) {
  return (
    <footer style={{ background: "var(--lp-primary)", color: "rgba(255,255,255,0.7)", padding: "40px 24px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 13 }}>{footer.copyright}</div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
          {(footer.links || []).map((l, i) => (
            <a key={i} href={l.url} style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>{l.label}</a>
          ))}
          <button
            type="button"
            onClick={() => window.__openCookieConsent && window.__openCookieConsent()}
            style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
            data-testid="footer-cookie-settings-landing"
          >
            Cookie-Einstellungen
          </button>
        </div>
      </div>
    </footer>
  );
}

function SectionHeader({ eyebrow, headline, subline, dark = false, centered = false }) {
  return (
    <div style={{ textAlign: centered ? "center" : "left", maxWidth: centered ? 760 : "none", margin: centered ? "0 auto" : 0 }}>
      {eyebrow && <p style={{ ...eyebrowStyle(), color: dark ? "var(--lp-accent)" : undefined }}>{eyebrow}</p>}
      <h2 style={{
        fontSize: "clamp(30px, 4.2vw, 52px)", fontWeight: 700, letterSpacing: -1, lineHeight: 1.1,
        marginTop: 14, marginBottom: subline ? 16 : 0, color: dark ? "#fff" : "var(--lp-text)",
      }}>{headline}</h2>
      {subline && <p style={{ fontSize: 17, lineHeight: 1.55, color: dark ? "rgba(255,255,255,0.7)" : "var(--lp-muted)", maxWidth: 720 }}>{subline}</p>}
    </div>
  );
}

/* ===== shared button styles ===== */
function eyebrowStyle() {
  return {
    fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
    color: "var(--lp-accent)", display: "inline-block",
  };
}

function btnSolid(size = "md") {
  return {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "var(--lp-primary)", color: "#fff",
    padding: size === "lg" ? "14px 24px" : "10px 18px",
    borderRadius: 999,
    fontSize: size === "lg" ? 15 : 14, fontWeight: 600,
    textDecoration: "none", border: "none", cursor: "pointer",
    transition: "transform 0.15s, box-shadow 0.15s",
  };
}

function btnOutline(size = "md") {
  return {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "transparent", color: "var(--lp-text)",
    padding: size === "lg" ? "14px 22px" : "10px 16px",
    borderRadius: 999,
    fontSize: size === "lg" ? 15 : 14, fontWeight: 600,
    textDecoration: "none", border: "1px solid rgba(0,0,0,0.18)", cursor: "pointer",
  };
}
